'use client';

import {
    type ChangeEvent,
    type ClipboardEvent,
    type FocusEvent,
    type KeyboardEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import Box from '@mui/material/Box';

import styles from './OtpCodeInput.module.scss';

type OtpCodeInputProps = {
    id?: string;
    label?: string;
    hint?: string;
    errorText?: string;
    dataTestId?: string;
    digitDataTestIdPrefix?: string;
    value: string;
    onChange: (nextValue: string) => void;
    onComplete?: (nextValue: string) => void;
    length?: number;
    autoFocus?: boolean;
};

const sanitizeOtpValue = (value: string, length: number) => value.replace(/[^\d]/g, '').slice(0, length);
const getOtpSlots = (value: string, length: number) => Array.from({ length }, (_, index) => value[index] ?? '');

const OtpCodeInputComponent = ({
    id = 'otp-code',
    label,
    hint,
    errorText,
    dataTestId,
    digitDataTestIdPrefix,
    value,
    onChange,
    onComplete,
    length = 4,
    autoFocus = false,
}: OtpCodeInputProps) => {
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const slots = useMemo(() => getOtpSlots(value, length), [length, value]);
    const fieldLabelTestId = dataTestId ? `${dataTestId}-label` : undefined;
    const codeFieldTestId = dataTestId ? `${dataTestId}-code-field` : undefined;
    const slotsTestId = dataTestId ? `${dataTestId}-slots` : undefined;
    const messageTestId = dataTestId ? `${dataTestId}-message` : undefined;

    const focusCell = useCallback((index: number) => {
        inputRefs.current[index]?.focus();
    }, []);

    const updateSlots = useCallback(
        (nextSlots: string[]) => {
            const nextValue = sanitizeOtpValue(nextSlots.join(''), length);
            onChange(nextValue);

            if (nextValue.length === length) {
                onComplete?.(nextValue);
            }
        },
        [length, onChange, onComplete]
    );

    const createCellChangeHandler = useCallback(
        (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
            const nextDigit = sanitizeOtpValue(event.target.value, 1);

            if (!nextDigit) {
                return;
            }

            const nextSlots = [...slots];
            nextSlots[index] = nextDigit;
            updateSlots(nextSlots);

            if (index < length - 1) {
                focusCell(index + 1);
            }
        },
        [focusCell, length, slots, updateSlots]
    );

    const createCellKeyDownHandler = useCallback(
        (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
            if (/^\d$/.test(event.key)) {
                event.preventDefault();

                const nextSlots = [...slots];
                nextSlots[index] = event.key;
                updateSlots(nextSlots);

                if (index < length - 1) {
                    focusCell(index + 1);
                }

                return;
            }

            if (event.key === 'Backspace') {
                event.preventDefault();

                const nextSlots = [...slots];

                if (nextSlots[index]) {
                    nextSlots[index] = '';
                    updateSlots(nextSlots);
                    return;
                }

                if (index > 0) {
                    nextSlots[index - 1] = '';
                    updateSlots(nextSlots);
                    focusCell(index - 1);
                }

                return;
            }

            if (event.key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                focusCell(index - 1);
                return;
            }

            if (event.key === 'ArrowRight' && index < length - 1) {
                event.preventDefault();
                focusCell(index + 1);
            }
        },
        [focusCell, length, slots, updateSlots]
    );

    const createCellPasteHandler = useCallback(
        (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
            const pastedValue = sanitizeOtpValue(event.clipboardData.getData('text'), length);

            if (!pastedValue) {
                return;
            }

            event.preventDefault();

            const nextSlots = [...slots];

            pastedValue.split('').forEach((digit, offset) => {
                const targetIndex = index + offset;

                if (targetIndex < length) {
                    nextSlots[targetIndex] = digit;
                }
            });

            updateSlots(nextSlots);
            focusCell(Math.min(index + pastedValue.length, length - 1));
        },
        [focusCell, length, slots, updateSlots]
    );

    const createCellFocusHandler = useCallback(
        (index: number) => (event: FocusEvent<HTMLInputElement>) => {
            setActiveIndex(index);
            event.currentTarget.select();
        },
        []
    );

    useEffect(() => {
        if (!autoFocus) {
            return;
        }

        const firstEmptyIndex = slots.findIndex((slot) => !slot);
        focusCell(firstEmptyIndex >= 0 ? firstEmptyIndex : length - 1);
    }, [autoFocus, focusCell, length, slots]);

    return (
        <Box className={styles.field} data-testid={dataTestId}>
            {label ? (
                <Box className={styles.label} component="label" htmlFor={id} data-testid={fieldLabelTestId}>
                    {label}
                </Box>
            ) : null}
            <Box className={styles.codeField} data-testid={codeFieldTestId}>
                <Box aria-label={label} className={styles.slots} role="group" data-testid={slotsTestId}>
                    {slots.map((slot, index) => (
                        <Box
                            component="input"
                            ref={(node: HTMLInputElement | null) => {
                                inputRefs.current[index] = node;
                            }}
                            aria-label={`${label ?? 'OTP code'} digit ${index + 1}`}
                            autoComplete={index === 0 ? 'one-time-code' : 'off'}
                            key={`${id}-${index}`}
                            className={styles.slot}
                            data-active={index === activeIndex}
                            data-filled={Boolean(slot)}
                            data-testid={digitDataTestIdPrefix ? `${digitDataTestIdPrefix}-${index + 1}` : undefined}
                            enterKeyHint={index === length - 1 ? 'done' : 'next'}
                            id={index === 0 ? id : undefined}
                            inputMode="numeric"
                            maxLength={1}
                            onChange={createCellChangeHandler(index)}
                            onFocus={createCellFocusHandler(index)}
                            onKeyDown={createCellKeyDownHandler(index)}
                            onPaste={createCellPasteHandler(index)}
                            value={slot}
                        />
                    ))}
                </Box>
            </Box>
            {errorText ? (
                <p aria-live="polite" className={styles.error} role="alert" data-testid={messageTestId}>
                    {errorText}
                </p>
            ) : hint ? (
                <p aria-live="polite" className={styles.hint} data-testid={messageTestId}>
                    {hint}
                </p>
            ) : null}
        </Box>
    );
};

OtpCodeInputComponent.displayName = 'OtpCodeInput';

export const OtpCodeInput = memo(OtpCodeInputComponent);
